import React, { useEffect, useRef } from 'react';
import {
    Camera,
    Copy,
    Home,
    Image as ImageIcon,
    LayoutGrid,
    type LucideIcon,
    Move3d,
    Scissors,
    Sofa,
    Sparkles,
    Video,
} from 'lucide-react';

import { CsRecipe } from '../../../../common/models/content-studio.model';
import { useSeedI18n } from '../hooks/use-seed-i18n.hook';
import { RecipeOptionCard } from './RecipeOptionCard';

interface RecipeSelectorProps {
    open: boolean;
    recipes: CsRecipe[];
    /** Canvas position where the selector should appear */
    anchorX: number;
    anchorY: number;
    /**
     * The product's visualization.type (surface | model3d | image_only).
     * Drives which recipes are hidden for this product family — surface
     * products don't surface lifestyle-room, remove-background, etc.
     */
    productVisualizationType?: string;
    onSelect: (recipe: CsRecipe) => void;
    onClose: () => void;
}

/**
 * Per-visualization-type recipe filter. Each product family's visualization
 * type dictates which recipes make sense:
 *   - surface (tiles / hardwood): material-focused recipes — flat-lay,
 *     styled scene, closeup, reference.
 *   - model3d (furniture): product-closeup + one room-staging recipe
 *     (lifestyle-scene, which has the furniture branch baked into the
 *     worker). lifestyle-room duplicates that job and is hidden for
 *     furniture to avoid confusion.
 *   - image_only (decor): fall through (show all) until we revisit.
 */
/**
 * Never offered anywhere — driven by a widget rather than a recipe card.
 *
 * Withdrawn recipes are NOT listed here any more. They carry `retired` from the
 * server, which is the only copy of that fact; keeping a second list in the
 * frontend is how flat-lay stayed reachable for `image_only` long after it was
 * meant to be gone — it was hidden per visualization type, and nobody thought
 * to add it to that one.
 */
export const ALWAYS_HIDDEN = new Set([
    'expand-image',
    // Retired. The comment below has claimed this was "retired outright in
    // ALWAYS_HIDDEN" for some time, but it was never actually added here and
    // was not named per type either — so the one recipe described as reachable
    // by nobody was reachable by everybody.
    'lifestyle-flatlay',
    // Superseded by lifestyle-scene for furniture and hidden for surface, which
    // left it reachable for no product type. Retired here rather than listed
    // per type, so a new visualization type cannot quietly inherit it.
    'lifestyle-room',
]);

/**
 * Recipe slugs a vendor can actually reach for a given product type.
 *
 * The picker hides retired and duplicated recipes per visualization type —
 * `lifestyle-room`, for instance, is superseded by `lifestyle-scene` and shown
 * to nobody — but they stay `enabled` in the database, so anything reading the
 * raw recipe list still sees them. The planner reads this instead, so it can
 * only propose what the vendor could have picked by hand.
 *
 * Unlike the picker, chain recipes (`requiresSourceAsset`) are kept: the picker
 * excludes them because it opens from a product node, but a plan can legitimately
 * chain one off an earlier step.
 *
 * MIRRORED in pms-api-v2 — `recipeIsAvailable` in pms-assistant/assistant-tools.ts,
 * which the backoffice assistant uses so it cannot propose a recipe a vendor
 * could not have picked. The two repos share no code, so a change to the rules
 * below needs the same change there; a test on that side pins the current
 * behaviour and will fail if only one copy moves.
 */
export function availableRecipeSlugs(
    recipes: Array<{ slug: string; retired?: boolean }>,
    visualizationType?: string,
): string[] {
    const hidden = visualizationType ? HIDDEN_BY_VIZ_TYPE[visualizationType] : undefined;
    return recipes
        .filter((r) => !r.retired)
        .map((r) => r.slug)
        // Place in a room is posed by hand in the placement view — a plan can
        // create the node but never finish it, so the planner is not offered it.
        .filter((slug) => !ALWAYS_HIDDEN.has(slug) && !hidden?.has(slug) && !HAND_POSED_RECIPES.has(slug));
}

/**
 * Recipes posed by hand in a view of their own.
 *
 * Place in a room is started from an image (the image's "+" menu), and its
 * product is placed by the vendor in the placement view. A plan could create
 * the node but never finish it, so the planner is not offered it.
 */
export const HAND_POSED_RECIPES = new Set(['place-in-room']);

export const HIDDEN_BY_VIZ_TYPE: Record<string, Set<string>> = {
    // Flat-lay is not listed per type any more — it is retired outright in
    // ALWAYS_HIDDEN. Naming it in both places is how it stayed reachable for
    // the one type nobody thought to add it to.
    surface: new Set([
        'lifestyle-room',
        'remove-background',
        'style-variation',
        'image-to-video',
        // Merged into "Room Scene" — the 3D-studio toggle switches In-Room (studio)
        // vs Lifestyle (visualizer pipeline). See SURFACE_CLOSEUP_CARDS below.
        'lifestyle-scene',
    ]),
    model3d: new Set([
        'lifestyle-room',   // duplicates lifestyle-scene for furniture
    ]),
};

/**
 * Preferred ordering per viz type — recipes listed here appear first,
 * in the given order. Recipes not in the list keep their server-side
 * `order`. For furniture we surface Product Photography at the top.
 */
const PRIORITY_ORDER_BY_VIZ_TYPE: Record<string, string[]> = {
    model3d: ['product-closeup', 'lifestyle-scene', 'from-reference'],
    surface: ['product-closeup', 'lifestyle-scene', 'from-reference'],
};

// Recipe glyphs. Emoji were dropped because they rendered inconsistently
// across operating systems and clashed with the app's neutral palette.
const RECIPE_ICONS: Record<string, LucideIcon> = {
    'lifestyle-room': Home,
    'lifestyle-flatlay': LayoutGrid,
    'lifestyle-scene': Sofa,
    'lifestyle-floor': Sofa, // legacy alias
    'product-closeup': Camera,
    'from-reference': ImageIcon,
    'remove-background': Scissors,
    'style-variation': Copy,
    'image-to-video': Video,
    'place-in-room': Move3d,
    // Surface split (both back the product-closeup slug — see CARD_ID below).
    'product-closeup-single': Camera,
    'product-closeup-applied': Home,
};

// Surface Product Photography is presented as TWO cards — "Single Piece" (the
// tile/plank on a backdrop) and "In-Room" (the material applied in a room/scene).
// Both create a product-closeup node; the card just locks inputs.surfaceMode, so
// the worker + backend are unchanged. `_cardId` / `_surfaceMode` ride along on a
// shallow-cloned recipe object.
type SurfaceCard = CsRecipe & { _cardId?: string; _surfaceMode?: 'single' | 'applied' };
const SURFACE_CLOSEUP_CARDS: Array<{ cardId: string; surfaceMode: 'single' | 'applied'; name: string; description: string }> = [
    { cardId: 'product-closeup-single', surfaceMode: 'single', name: 'Single Piece', description: 'Photograph the tile / plank / slab itself on a styled backdrop.' },
    // Room Scene merges In-Room (3D studio ON) + Lifestyle (3D studio OFF → the
    // depth/visualizer pipeline). The 3D-studio toggle inside the node switches them.
    { cardId: 'product-closeup-applied', surfaceMode: 'applied', name: 'Room Scene', description: 'Show the material in a real room — design the shot in 3D, or auto-generate lifestyle scenes.' },
];

/**
 * A single recipe card — thin wrapper over the shared RecipeOptionCard that
 * resolves the recipe's icon and forwards the click.
 */
const RecipeCard: React.FC<{
    recipe: CsRecipe;
    index: number;
    name: string;
    description: string;
    onSelect: (r: CsRecipe) => void;
}> = ({ recipe, index, name, description, onSelect }) => {
    const Icon = RECIPE_ICONS[(recipe as SurfaceCard)._cardId || recipe.slug] || Sparkles;
    return (
        <RecipeOptionCard
            index={index}
            icon={<Icon size={20} strokeWidth={1.8} />}
            title={name}
            description={description}
            onClick={() => onSelect(recipe)}
        />
    );
};

/**
 * RPG-style recipe selector — fans out as a vertical stack of cards
 * from the product node. Each card shows the recipe name, description,
 * credit cost, and a representative icon. Clicking one creates
 * the recipe node. Clicking outside closes.
 */
export const RecipeSelector: React.FC<RecipeSelectorProps> = ({
    open,
    recipes,
    anchorX,
    anchorY,
    productVisualizationType,
    onSelect,
    onClose,
}) => {
    const seedI18n = useSeedI18n();
    const containerRef = useRef<HTMLDivElement>(null);

    // Recipes that are invoked from places other than the picker (e.g.
    // the Edit Image modal on a generation node) — hide universally.
    const HIDDEN_FROM_PICKER = ALWAYS_HIDDEN;
    const hidden = productVisualizationType
        ? HIDDEN_BY_VIZ_TYPE[productVisualizationType]
        : undefined;
    // Chain recipes (requiresSourceAsset=true) run off a generation
    // node, not a product node. They're invoked via the hover pills on
    // a generation (Variation / Edit image / Video) — never from this
    // picker, which is only opened from product nodes.
    const filtered = recipes.filter((r) => {
        if (r.retired) return false;
        if (HIDDEN_FROM_PICKER.has(r.slug)) return false;
        if (hidden && hidden.has(r.slug)) return false;
        if (r.requiresSourceAsset) return false;
        return true;
    });

    // Apply the priority order — recipes in the priority list come
    // first in the given order; everything else keeps its server-side
    // `order`. For furniture this puts Product Photography at the top.
    const priorityList = productVisualizationType
        ? PRIORITY_ORDER_BY_VIZ_TYPE[productVisualizationType] || []
        : [];
    const priorityIndex = new Map(priorityList.map((slug, i) => [slug, i] as const));
    const sortedRecipes = [...filtered].sort((a, b) => {
        const ai = priorityIndex.has(a.slug) ? priorityIndex.get(a.slug)! : Number.POSITIVE_INFINITY;
        const bi = priorityIndex.has(b.slug) ? priorityIndex.get(b.slug)! : Number.POSITIVE_INFINITY;
        if (ai !== bi) return ai - bi;
        return (a.order ?? 0) - (b.order ?? 0);
    });
    // For surface products, split Product Photography into two cards (Single Piece
    // / In-Room). Both keep the product-closeup slug; the card only locks the shot
    // type via `_surfaceMode` (read in handleSelectRecipe → inputs.surfaceMode).
    const visibleRecipes: SurfaceCard[] = [];
    for (const r of sortedRecipes) {
        if (productVisualizationType === 'surface' && r.slug === 'product-closeup') {
            for (const c of SURFACE_CLOSEUP_CARDS) {
                visibleRecipes.push({ ...r, _cardId: c.cardId, _surfaceMode: c.surfaceMode, name: c.name, description: c.description });
            }
        } else {
            visibleRecipes.push(r);
        }
    }

    // Close on click anywhere outside (document-level, catches clicks outside canvas-space)
    useEffect(() => {
        if (!open) return;
        const handler = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                onClose();
            }
        };
        const timer = setTimeout(() => document.addEventListener('mousedown', handler), 50);
        return () => { clearTimeout(timer); document.removeEventListener('mousedown', handler); };
    }, [open, onClose]);

    if (!open) return null;

    return (
            <div
                ref={containerRef}
                onMouseDown={(e) => e.stopPropagation()}
                className="absolute z-[41] flex w-[260px] flex-col gap-2"
                style={{ left: anchorX, top: anchorY }}
            >
                {visibleRecipes.map((recipe, i) => (
                    <RecipeCard
                        key={recipe._cardId || recipe.slug}
                        recipe={recipe}
                        index={i}
                        name={recipe._cardId ? recipe.name : seedI18n.recipeName(recipe.slug, recipe.name)}
                        description={recipe._cardId ? recipe.description : seedI18n.recipeDescription(recipe.slug, recipe.description)}
                        onSelect={onSelect}
                    />
                ))}
            </div>
    );
};
